<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1400px"
    :fullscreen="$vuetify.display.mdAndDown"
    persistent
    scrollable
    @update:model-value="handleDialogChange"
  >
    <v-card
      class="office-selection-dialog"
      :class="{ 'fullscreen-dialog': $vuetify.display.mdAndDown }"
    >
      <v-toolbar
        color="primary"
        dark
        flat
      >
        <v-toolbar-title class="dialog-title text-white">{{
          $t('content-creator.office-selection.destination-selection')
        }}</v-toolbar-title>
        <v-spacer />
        <v-btn
          icon
          @click="handleClose"
        >
          <v-icon class="text-white">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0 dialog-content">
        <v-row
          no-gutters
          class="fill-height"
        >
          <v-col
            cols="12"
            sm="7"
            class="pa-6 table-column"
          >
            <OfficeFilterBar
              v-model:include-head-office="includeHeadOffice"
              :unique-states="uniqueStates"
              :selected-states="selectedStates"
              :available-cities="availableCities"
              :selected-cities="selectedCities"
              :is-social-media-connected="props.isSocialMediaConnected"
              @toggle-state="handleToggleState"
              @toggle-city="handleToggleCity"
              @clear-filters="clearFilters"
            />

            <OfficeTable
              v-model:selected-offices="selectedOffices"
              v-model:current-page="currentPage"
              :offices="filteredOffices"
              :loading="loading"
              :visible-pages="visiblePages"
              :total-pages="totalPages"
              :items-per-page="itemsPerPage"
            />
          </v-col>

          <v-col
            v-if="$vuetify.display.smAndUp"
            cols="5"
          >
            <OfficePreview
              :selected-offices="selectedOffices"
              :include-head-office="includeHeadOffice"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-6 v-card-actions">
        <v-btn
          variant="text"
          class="action-btn"
          @click="handleClose"
        >
          {{ $t('content-creator.office-selection.back') }}
        </v-btn>
        <v-spacer />
        <v-btn
          variant="text"
          class="action-btn"
          :disabled="!includeHeadOffice && selectedOffices.length === 0"
          @click="handleConfirm"
        >
          {{ $t('content-creator.office-selection.confirm-selection') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

import OfficeFilterBar from './components/OfficeFilterBar.vue'
import OfficePreview from './components/OfficePreview.vue'
import OfficeTable from './components/OfficeTable.vue'
import { Office, useOfficeSelection } from './composables/useOfficeSelection'
import { usePagination } from './composables/usePagination'

interface Props {
  modelValue: boolean
  initialSelectedOffices?: Office[]
  initialIncludeHeadOffice?: boolean
  isSocialMediaConnected?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  initialSelectedOffices: undefined,
  initialIncludeHeadOffice: false,
  isSocialMediaConnected: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [data: { offices: Office[]; includeHeadOffice: boolean }]
}>()

const {
  loading,
  offices,
  filteredOffices,
  uniqueStates,
  availableCities,
  selectedOffices,
  selectedStates,
  selectedCities,
  includeHeadOffice,
  toggleState,
  toggleCity,
  clearFilters,
  setInitialValues,
} = useOfficeSelection()

const totalItems = computed(() => filteredOffices.value.length)
const { currentPage, totalPages, visiblePages, itemsPerPage, resetPage } = usePagination(totalItems)

const handleToggleState = (state: string) => {
  toggleState(state)
  resetPage()
}

const handleToggleCity = (city: string) => {
  toggleCity(city)
  resetPage()
}

const handleDialogChange = (value: boolean) => {
  emit('update:modelValue', value)
}

const handleClose = () => {
  emit('update:modelValue', false)
}

const handleConfirm = () => {
  emit('confirm', {
    offices: selectedOffices.value,
    includeHeadOffice: includeHeadOffice.value,
  })
  emit('update:modelValue', false)
}

// When dialog opens, restore from props or select all by default
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      resetPage()
      // Clear filters when opening
      selectedStates.value = []
      selectedCities.value = []

      // If initial values are provided, use them
      if (props.initialSelectedOffices !== undefined) {
        setInitialValues(props.initialSelectedOffices, props.initialIncludeHeadOffice)
      } else if (offices.value.length > 0) {
        // Otherwise select all offices by default
        selectedOffices.value = [...offices.value]
        includeHeadOffice.value = false
      }
    }
  }
)

// Also handle when offices load after dialog is already open
watch(
  () => offices.value,
  (newOffices) => {
    if (props.modelValue && newOffices.length > 0 && selectedOffices.value.length === 0) {
      // Dialog is open and offices just loaded - select all if no initial selection
      if (props.initialSelectedOffices === undefined) {
        selectedOffices.value = [...newOffices]
      }
    }
  }
)
</script>

<style scoped>
.office-selection-dialog {
  display: flex;
  flex-direction: column;
  height: 90vh;
  max-height: 90vh;
  min-height: 90vh;
}

.office-selection-dialog.fullscreen-dialog {
  height: 100vh;
  max-height: 100vh;
  min-height: 100vh;
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
}

.dialog-content {
  flex: 1 1 0;
  overflow: hidden;
  display: flex;
  min-height: 0;
}

.dialog-content :deep(.v-row) {
  height: 100%;
  margin: 0;
  flex: 1 1 auto;
}

.dialog-content :deep(.v-col) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.table-column {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  flex: 1 1 0;
}

.action-btn {
  color: rgba(var(--v-theme-on-surface)) !important;
  font-weight: 500;
}

.action-btn:hover {
  background-color: rgba(16, 42, 33, 0.04);
}

.action-btn:disabled {
  opacity: 0.5;
}
.v-card-actions {
  margin-bottom: 50px;
}
</style>
